/**
 * Compliance Purpose Item Drawer Component
 * 
 * Complete drawer solution for adding/editing compliance purposes including:
 * - Specific Fraud Risk Factors field (required, plain text area)
 * - Objectives cross-process selection (Cycle -> Process -> Objectives)
 * - Fraud Risk Mitigating Controls multi-select (from EX 5)
 * - Form validation
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Form, Input, Dropdown } from 'antd';
import { RightOutlined, SearchOutlined, CheckOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { RootState, AppDispatch } from '../../../../store';
import { setObjectives } from '../../../../store/slices/riskAssessmentSlice';
import {
  getObjectives,
  parseObjectives,
  Cycle,
  Objective,
} from '../../../../services/riskAssessmentService';
import RightContentDrawer from '../../../../components/common/RightContentDrawer';
import ChevronDownIcon from '../../../../assets/icons/ic-chevron-down.svg?react';
import ButtonGroup from '../../../../components/common/ButtonGroup';
import { htmlToPlainText } from '../../../../utils/exportUtils';

const { TextArea } = Input;

// ========== TYPE DEFINITIONS ==========

export interface CompliancePurposeFormData {
  id?: string;
  fraudRiskNumber?: string;
  specificFraudRiskFactors: string;
  objectives: string[];
  objectiveNumbers?: string[];
  objectiveProcessCodes?: string[];
  fraudRiskMitigatingControls: string[];
}

export interface CompliancePurposeItemDrawerProps {
  open: boolean;
  mode: 'add' | 'edit';
  initialValues?: Partial<CompliancePurposeFormData>;
  onClose: () => void;
  onSubmit: (values: CompliancePurposeFormData) => Promise<void>;
}

// ========== STYLED COMPONENTS ==========

const FormLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.005em;
  color: #1F2937;
  margin-bottom: 10px;
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  gap: 20px;

  .ant-form-item {
    margin-bottom: 0 !important;
  }
` as typeof Form;

const StyledTextArea = styled(TextArea)`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  border-radius: 8px !important;
  letter-spacing: 0.005em;
  color: #6B7280;
  min-height: 120px !important;

  &.ant-input {
    color: #6B7280;
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const InfoText = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  color: #6B7280;
  margin-top: 6px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 0;

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #E5E7EB;
    border-top-color: #2563EB;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .text {
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    color: #6B7280;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Tag Container (form view - shows selected objectives)
const TagContainer = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 8px 32px 8px 12px;
  min-height: 44px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  background: white;
  cursor: pointer;

  &:hover {
    border-color: #2563EB;
  }
`;

const ArrowButton = styled.div`
  position: absolute;
  top: 50%;
  right: 4px;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 8px;
  
  .anticon {
    color: #6B7280;
    font-size: 12px;
    transition: color 0.2s;
  }

  &:hover .anticon {
    color: #2563EB;
  }
`;

const SelectedObjectiveTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  margin: 2px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #DBEAFE;
  }
`;

const ObjectiveProcessCode = styled.span`
  font-weight: 600;
  margin-right: 4px;
`;

const ObjectiveNumbersGroup = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ObjectiveNumberCircle = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  background: transparent;
  color: #2563EB;
  border: 1px solid #D1D5DB;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 600;
`;

const PlaceholderText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #9CA3AF;
`;

// Search Mode Components
const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SelectorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const SelectorLabel = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
`;

const SelectorButton = styled.div<{ $hasValue?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  min-height: 44px;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #2563EB;
  }
`;

const SelectorValueText = styled.span<{ $placeholder?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$placeholder ? '#9CA3AF' : '#1F2937'};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChevronIcon = styled.div`
  display: flex;
  align-items: center;
  color: #6B7280;
  
  svg {
    width: 16px;
    height: 16px;
  }
  
  svg path {
    stroke: #6B7280;
  }
`;

const DropdownMenu = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  padding: 8px;
  width: 346px;
  max-height: 360px;
  overflow-y: auto;
`;

const SearchInput = styled(Input)`
  margin-bottom: 8px;
  border-radius: 6px;
  height: 38px;
  
  .ant-input {
    font-family: Inter;
    font-size: 15px;
  }
`;

const DropdownSectionLabel = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  color: #6B7280;
  padding: 8px 12px;
`;

const DropdownItem = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'white'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F3F4F6'};
  }
`;

const DropdownItemText = styled.span<{ $selected?: boolean }>`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: ${props => props.$selected ? '#2563EB' : '#1F2937'};
  flex: 1;
`;

const ObjectivesDivider = styled.div`
  height: 1px;
  background: #E5E7EB;
  margin: 8px 0 16px 0;
`;

const ObjectivesListTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #1F2937;
  margin-bottom: 12px;
`;

const ObjectivesList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const ObjectiveListItem = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#EFF6FF' : 'transparent'};
  border: 1px solid ${props => props.$selected ? '#2563EB' : '#E5E7EB'};

  &:hover {
    background: ${props => props.$selected ? '#EFF6FF' : '#F9FAFB'};
  }
`;

const ObjectiveCheckbox = styled.div<{ $checked: boolean }>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 4px;
  border: 2px solid ${props => props.$checked ? '#2563EB' : '#D1D5DB'};
  background: ${props => props.$checked ? '#2563EB' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  transition: all 0.2s;
  color: white;
  font-size: 12px;
`;

const ObjectiveItemText = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  color: #1F2937;
  flex: 1;
  word-wrap: break-word;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #9CA3AF;
  font-family: Inter;
  font-size: 14px;
`;

// ========== MAIN COMPONENT ==========

const CompliancePurposeItemDrawer: React.FC<CompliancePurposeItemDrawerProps> = ({
  open,
  mode,
  initialValues,
  onClose,
  onSubmit,
}) => {
  const { id: assessmentId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  // Redux state
  const objectivesData = useSelector((state: RootState) => state.riskAssessment.objectives);
  const riskAssessmentCycles = useSelector((state: RootState) => state.riskAssessment.cycles);
  
  // Form states
  const [form] = Form.useForm();
  const [specificFraudRiskFactors, setSpecificFraudRiskFactors] = useState('');
  const [objectives, setObjectivesLocal] = useState<string[]>([]);
  const [fraudRiskMitigatingControls, setFraudRiskMitigatingControls] = useState<string[]>([]);

  // UI states
  const [loading, setLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoadingObjectives, setIsLoadingObjectives] = useState(false);

  // Search mode states
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [selectedSearchCycleId, setSelectedSearchCycleId] = useState<string | null>(null);
  const [selectedSearchAssessmentId, setSelectedSearchAssessmentId] = useState<string | null>(null);
  const [searchObjectives, setSearchObjectives] = useState<Objective[]>([]);
  const [isLoadingSearchObjectives, setIsLoadingSearchObjectives] = useState(false);
  const [tempSelectedObjectives, setTempSelectedObjectives] = useState<string[]>([]);
  const [cycleSearchTerm, setCycleSearchTerm] = useState('');
  const [processSearchTerm, setProcessSearchTerm] = useState('');
  const [objectiveMetaMap, setObjectiveMetaMap] = useState<Record<string, { processCode: string; objectiveNumber: string; assessmentId: string }>>({});
  const [cycleDropdownOpen, setCycleDropdownOpen] = useState(false);
  const [processDropdownOpen, setProcessDropdownOpen] = useState(false);

  // Ref for auto-opening process dropdown after cycle change
  const shouldAutoOpenProcess = useRef(false);

  // Derive current cycle and assessment from Redux
  const currentCycleAndAssessment = useMemo(() => {
    if (!assessmentId || riskAssessmentCycles.length === 0) return null;
    for (const cycle of riskAssessmentCycles) {
      const assessment = cycle.riskAssessments.find(ra => ra.id === assessmentId);
      if (assessment) {
        return { cycle, assessment };
      }
    }
    return null;
  }, [assessmentId, riskAssessmentCycles]);

  // Selected cycle object in search mode
  const selectedSearchCycle = useMemo<Cycle | null>(() => {
    if (!selectedSearchCycleId) return null;
    return riskAssessmentCycles.find(c => c.id === selectedSearchCycleId) || null;
  }, [selectedSearchCycleId, riskAssessmentCycles]);

  // Filtered cycles for search
  const filteredCycles = useMemo(() => {
    if (!cycleSearchTerm) return riskAssessmentCycles;
    const term = cycleSearchTerm.toLowerCase();
    return riskAssessmentCycles.filter(cycle =>
      cycle.cycleCode.toLowerCase().includes(term) ||
      cycle.cycleName.toLowerCase().includes(term)
    );
  }, [cycleSearchTerm, riskAssessmentCycles]);

  // Available processes for selected cycle in search mode
  const availableProcesses = useMemo(() => {
    if (!selectedSearchCycle) return [];
    return selectedSearchCycle.riskAssessments;
  }, [selectedSearchCycle]);

  // Filtered processes for search
  const filteredProcesses = useMemo(() => {
    if (!processSearchTerm) return availableProcesses;
    const term = processSearchTerm.toLowerCase();
    return availableProcesses.filter(ra =>
      ra.processCode.toLowerCase().includes(term) ||
      ra.processName.toLowerCase().includes(term)
    );
  }, [processSearchTerm, availableProcesses]);

  // Selected process/assessment object in search mode
  const selectedSearchAssessment = useMemo(() => {
    if (!selectedSearchAssessmentId || !selectedSearchCycle) return null;
    return selectedSearchCycle.riskAssessments.find(ra => ra.id === selectedSearchAssessmentId) || null;
  }, [selectedSearchAssessmentId, selectedSearchCycle]);

  // Fetch objectives for current assessment into Redux (existing behavior, unchanged)
  useEffect(() => {
    const fetchObjectivesIfNeeded = async () => {
      if (!open || !assessmentId) return;
      if (objectivesData.length > 0) return;

      setIsLoadingObjectives(true);
      try {
        const response = await getObjectives(assessmentId, {
          skip: 0,
          take: 1000,
          requiresCounts: true,
        });
        const parsedObjectives = parseObjectives(response.result || []);
        dispatch(setObjectives(parsedObjectives));
      } catch (error) {
        console.error('Failed to fetch objectives for picker:', error);
      } finally {
        setIsLoadingObjectives(false);
      }
    };

    fetchObjectivesIfNeeded();
  }, [open, assessmentId, objectivesData.length, dispatch]);

  // Build meta map from Redux objectives (current assessment)
  useEffect(() => {
    if (objectivesData.length > 0 && currentCycleAndAssessment) {
      const processCode = currentCycleAndAssessment.assessment.processCode;
      const aId = currentCycleAndAssessment.assessment.id;
      setObjectiveMetaMap(prev => {
        const updated = { ...prev };
        objectivesData.forEach(obj => {
          updated[obj.id] = {
            processCode,
            objectiveNumber: obj.index.toString().padStart(2, '0'),
            assessmentId: aId,
          };
        });
        return updated;
      });
    }
  }, [objectivesData, currentCycleAndAssessment]);

  // Fetch objectives when process changes in search mode
  const fetchSearchObjectives = useCallback(async (targetAssessmentId: string) => {
    const isCurrentAssessment = targetAssessmentId === assessmentId;

    if (isCurrentAssessment && objectivesData.length > 0) {
      setSearchObjectives(objectivesData);
      return;
    }

    setSearchObjectives([]);
    setIsLoadingSearchObjectives(true);
    try {
      const response = await getObjectives(targetAssessmentId, {
        skip: 0,
        take: 1000,
        requiresCounts: true,
      });
      const parsed = parseObjectives(response.result || []);
      setSearchObjectives(parsed);

      if (isCurrentAssessment) {
        dispatch(setObjectives(parsed));
      }

      // Accumulate meta for these objectives
      const targetAssessment = riskAssessmentCycles
        .flatMap(c => c.riskAssessments)
        .find(ra => ra.id === targetAssessmentId);
      if (targetAssessment) {
        const processCode = targetAssessment.processCode;
        setObjectiveMetaMap(prev => {
          const updated = { ...prev };
          parsed.forEach(obj => {
            updated[obj.id] = {
              processCode,
              objectiveNumber: obj.index.toString().padStart(2, '0'),
              assessmentId: targetAssessmentId,
            };
          });
          return updated;
        });
      }
    } catch (error) {
      console.error('Failed to fetch objectives for search:', error);
      setSearchObjectives([]);
    } finally {
      setIsLoadingSearchObjectives(false);
    }
  }, [assessmentId, objectivesData, dispatch, riskAssessmentCycles]);

  // Reset form when initialValues change
  useEffect(() => {
    if (mode === 'edit' && initialValues) {
      setSpecificFraudRiskFactors(initialValues.specificFraudRiskFactors || '');
      setObjectivesLocal(initialValues.objectives || []);
      setFraudRiskMitigatingControls(initialValues.fraudRiskMitigatingControls || []);
      
      form.setFieldsValue({
        specificFraudRiskFactors: initialValues.specificFraudRiskFactors,
        objectives: initialValues.objectives,
        fraudRiskMitigatingControls: initialValues.fraudRiskMitigatingControls,
      });

      // Seed objectiveMetaMap from initialValues parallel arrays
      if (initialValues.objectives && initialValues.objectiveNumbers && initialValues.objectiveProcessCodes) {
        const ids = initialValues.objectives;
        const nums = initialValues.objectiveNumbers;
        const codes = initialValues.objectiveProcessCodes;
        setObjectiveMetaMap(prev => {
          const updated = { ...prev };
          ids.forEach((objId, idx) => {
            if (!updated[objId] && codes[idx] && nums[idx]) {
              // Resolve assessmentId from cycles for this process code
              const ra = riskAssessmentCycles
                .flatMap(c => c.riskAssessments)
                .find(r => r.processCode === codes[idx]);
              updated[objId] = {
                processCode: codes[idx],
                objectiveNumber: nums[idx],
                assessmentId: ra?.id || '',
              };
            }
          });
          return updated;
        });
      }
    } else {
      resetForm();
    }
  }, [open, mode, initialValues]);

  // Watch for field changes and validate
  useEffect(() => {
    const hasSpecificFraudRiskFactors = !!specificFraudRiskFactors && specificFraudRiskFactors.trim() !== '';
    setIsFormValid(hasSpecificFraudRiskFactors);
  }, [specificFraudRiskFactors]);

  // Auto-open process dropdown after cycle change
  useEffect(() => {
    if (shouldAutoOpenProcess.current && selectedSearchCycleId && !selectedSearchAssessmentId) {
      shouldAutoOpenProcess.current = false;
      setTimeout(() => setProcessDropdownOpen(true), 150);
    }
  }, [selectedSearchCycleId, selectedSearchAssessmentId]);

  const resetForm = () => {
    setSpecificFraudRiskFactors('');
    setObjectivesLocal([]);
    setFraudRiskMitigatingControls([]);
    form.resetFields();
  };

  // ===== SEARCH MODE LOGIC =====

  const enterObjectiveSearchMode = () => {
    setIsSearchMode(true);
    setCycleSearchTerm('');
    setProcessSearchTerm('');
    setTempSelectedObjectives([...objectives]);

    if (currentCycleAndAssessment) {
      setSelectedSearchCycleId(currentCycleAndAssessment.cycle.id);
      setSelectedSearchAssessmentId(assessmentId || null);

      // Use Redux objectives for current assessment
      if (objectivesData.length > 0) {
        setSearchObjectives(objectivesData);
      } else if (assessmentId) {
        fetchSearchObjectives(assessmentId);
      }
    } else {
      setSelectedSearchCycleId(null);
      setSelectedSearchAssessmentId(null);
      setSearchObjectives([]);
    }
  };

  const exitObjectiveSearchMode = () => {
    setIsSearchMode(false);
    setCycleSearchTerm('');
    setProcessSearchTerm('');
    setSearchObjectives([]);
    setSelectedSearchCycleId(null);
    setSelectedSearchAssessmentId(null);
    setTempSelectedObjectives([]);
    setCycleDropdownOpen(false);
    setProcessDropdownOpen(false);
  };

  const applyObjectiveSelection = () => {
    setObjectivesLocal(tempSelectedObjectives);
    exitObjectiveSearchMode();
  };

  const handleCycleSelect = (cycleId: string) => {
    setCycleSearchTerm('');
    setCycleDropdownOpen(false);
    setSelectedSearchCycleId(cycleId);

    // Clear process and objectives, auto-open process dropdown
    setSelectedSearchAssessmentId(null);
    setSearchObjectives([]);
    setProcessSearchTerm('');
    shouldAutoOpenProcess.current = true;
  };

  const handleProcessSelect = (raAssessmentId: string) => {
    setProcessSearchTerm('');
    setProcessDropdownOpen(false);
    setSelectedSearchAssessmentId(raAssessmentId);
    fetchSearchObjectives(raAssessmentId);
  };

  const toggleObjectiveSelection = (objectiveId: string) => {
    setTempSelectedObjectives(prev =>
      prev.includes(objectiveId)
        ? prev.filter(id => id !== objectiveId)
        : [...prev, objectiveId]
    );
  };

  const handleClose = () => {
    setIsFormValid(false);
    setIsSearchMode(false);
    resetForm();
    setObjectiveMetaMap({});
    onClose();
  };

  const handleDrawerClose = () => {
    if (isSearchMode) {
      exitObjectiveSearchMode();
    } else {
      handleClose();
    }
  };

  const handleFormSubmit = async () => {
    setLoading(true);
    try {
      const formData: CompliancePurposeFormData = {
        specificFraudRiskFactors,
        objectives,
        fraudRiskMitigatingControls,
      };

      if (mode === 'edit' && initialValues) {
        formData.id = initialValues.id;
      }

      await onSubmit(formData);
      handleClose();
    } catch (error) {
      console.error("Form submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const drawerTitle = mode === 'add'
    ? 'Add Fraud Risk Factor'
    : `Edit Fraud Risk Factor${initialValues?.fraudRiskNumber ? ` - ${initialValues.fraudRiskNumber}` : ''}`;

  // ===== CYCLE DROPDOWN =====

  const cycleDropdownContent = (
    <DropdownMenu onClick={(e) => e.stopPropagation()}>
      <SearchInput
        prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
        placeholder="Search cycle"
        value={cycleSearchTerm}
        onChange={(e) => setCycleSearchTerm(e.target.value)}
        autoFocus
      />
      {cycleSearchTerm === '' && (
        <DropdownSectionLabel>All cycles</DropdownSectionLabel>
      )}
      {filteredCycles.map(cycle => {
        const isSelected = selectedSearchCycleId === cycle.id;
        return (
          <DropdownItem
            key={cycle.id}
            $selected={isSelected}
            onClick={() => handleCycleSelect(cycle.id)}
          >
            <DropdownItemText $selected={isSelected}>
              {cycle.cycleCode} - {cycle.cycleName}
            </DropdownItemText>
          </DropdownItem>
        );
      })}
      {filteredCycles.length === 0 && (
        <EmptyState style={{ padding: '16px' }}>No cycles found</EmptyState>
      )}
    </DropdownMenu>
  );

  // ===== PROCESS DROPDOWN =====

  const processDropdownContent = (
    <DropdownMenu onClick={(e) => e.stopPropagation()}>
      <SearchInput
        prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
        placeholder="Search process"
        value={processSearchTerm}
        onChange={(e) => setProcessSearchTerm(e.target.value)}
        autoFocus
      />
      {processSearchTerm === '' && (
        <DropdownSectionLabel>
          {selectedSearchCycle ? `Processes in ${selectedSearchCycle.cycleCode}` : 'All processes'}
        </DropdownSectionLabel>
      )}
      {filteredProcesses.map(ra => {
        const isSelected = selectedSearchAssessmentId === ra.id;
        return (
          <DropdownItem
            key={ra.id}
            $selected={isSelected}
            onClick={() => handleProcessSelect(ra.id)}
          >
            <DropdownItemText $selected={isSelected}>
              {ra.processCode} - {ra.processName}
            </DropdownItemText>
          </DropdownItem>
        );
      })}
      {filteredProcesses.length === 0 && (
        <EmptyState style={{ padding: '16px' }}>
          {selectedSearchCycleId ? 'No processes found' : 'Select a cycle first'}
        </EmptyState>
      )}
    </DropdownMenu>
  );

  // ===== RENDER: SEARCH MODE UI =====

  const renderObjectiveSearchUI = () => {
    return (
      <SearchContainer>
        <SelectorSection>
          {/* Cycle Selector */}
          <div>
            <SelectorLabel>Cycle</SelectorLabel>
            <Dropdown
              dropdownRender={() => cycleDropdownContent}
              trigger={['click']}
              placement="bottomLeft"
              open={cycleDropdownOpen}
              onOpenChange={(open) => {
                setCycleDropdownOpen(open);
                if (!open) setCycleSearchTerm('');
              }}
            >
              <SelectorButton $hasValue={!!selectedSearchCycle}>
                <SelectorValueText $placeholder={!selectedSearchCycle}>
                  {selectedSearchCycle
                    ? `${selectedSearchCycle.cycleCode} - ${selectedSearchCycle.cycleName}`
                    : 'Select cycle'}
                </SelectorValueText>
                <ChevronIcon>
                  <ChevronDownIcon />
                </ChevronIcon>
              </SelectorButton>
            </Dropdown>
          </div>

          {/* Process Selector */}
          <div>
            <SelectorLabel>Process</SelectorLabel>
            <Dropdown
              dropdownRender={() => processDropdownContent}
              trigger={['click']}
              placement="bottomLeft"
              open={processDropdownOpen}
              onOpenChange={(open) => {
                setProcessDropdownOpen(open);
                if (!open) setProcessSearchTerm('');
              }}
              disabled={!selectedSearchCycleId}
            >
              <SelectorButton
                $hasValue={!!selectedSearchAssessment}
                style={{
                  opacity: selectedSearchCycleId ? 1 : 0.5,
                  cursor: selectedSearchCycleId ? 'pointer' : 'not-allowed',
                }}
              >
                <SelectorValueText $placeholder={!selectedSearchAssessment}>
                  {selectedSearchAssessment
                    ? `${selectedSearchAssessment.processCode} - ${selectedSearchAssessment.processName}`
                    : 'Select process'}
                </SelectorValueText>
                <ChevronIcon>
                  <ChevronDownIcon />
                </ChevronIcon>
              </SelectorButton>
            </Dropdown>
          </div>
        </SelectorSection>

        <ObjectivesDivider />

        {/* Objectives List */}
        <ObjectivesListTitle>
          Objectives
          {selectedSearchAssessment && !isLoadingSearchObjectives && searchObjectives.length > 0 && (
            <span style={{ fontWeight: 400, color: '#6B7280', fontSize: 13 }}>
              {' '}({searchObjectives.length})
            </span>
          )}
        </ObjectivesListTitle>

        <ObjectivesList>
          {!selectedSearchAssessmentId ? (
            <EmptyState>Select a process to view objectives</EmptyState>
          ) : isLoadingSearchObjectives ? (
            <LoadingWrapper>
              <div className="spinner" />
              <div className="text">Loading objectives...</div>
            </LoadingWrapper>
          ) : searchObjectives.length === 0 ? (
            <EmptyState>No objectives available for this process</EmptyState>
          ) : (
            searchObjectives.map(obj => {
              const isChecked = tempSelectedObjectives.includes(obj.id);
              const label = `${obj.index.toString().padStart(2, '0')} - ${htmlToPlainText(obj.objectives)}`;
              return (
                <ObjectiveListItem
                  key={obj.id}
                  $selected={isChecked}
                  onClick={() => toggleObjectiveSelection(obj.id)}
                >
                  <ObjectiveCheckbox $checked={isChecked}>
                    {isChecked && <CheckOutlined />}
                  </ObjectiveCheckbox>
                  <ObjectiveItemText>{label}</ObjectiveItemText>
                </ObjectiveListItem>
              );
            })
          )}
        </ObjectivesList>
      </SearchContainer>
    );
  };

  // ===== RENDER: FORM UI =====

  const handleObjectiveChipClick = (chipAssessmentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/risk-assessment/${chipAssessmentId}/tab2`, {
      replace: true,
    });
  };

  const renderObjectiveTags = () => {
    if (objectives.length === 0) {
      return <PlaceholderText>Select objectives</PlaceholderText>;
    }

    // Group objectives by processCode (like EX5 pattern)
    const grouped: Record<string, { assessmentId: string; numbers: { id: string; num: string }[] }> = {};
    objectives.forEach(objId => {
      const meta = objectiveMetaMap[objId];
      if (meta) {
        if (!grouped[meta.processCode]) {
          grouped[meta.processCode] = { assessmentId: meta.assessmentId, numbers: [] };
        }
        grouped[meta.processCode].numbers.push({ id: objId, num: meta.objectiveNumber });
      }
    });

    const groupEntries = Object.entries(grouped);

    if (groupEntries.length === 0) {
      return <PlaceholderText>{objectives.length} objective(s) selected</PlaceholderText>;
    }

    return (
      <>
        {groupEntries.map(([processCode, { assessmentId: aId, numbers }]) => (
          <SelectedObjectiveTag
            key={processCode}
            onClick={(e) => handleObjectiveChipClick(aId, e)}
            title={`Click to view objectives for ${processCode}`}
          >
            <ObjectiveProcessCode>{processCode}</ObjectiveProcessCode>
            <ObjectiveNumbersGroup>
              {[...numbers]
                .sort((a, b) => parseInt(a.num, 10) - parseInt(b.num, 10))
                .map(({ id, num }) => (
                  <ObjectiveNumberCircle key={id}>{num}</ObjectiveNumberCircle>
                ))}
            </ObjectiveNumbersGroup>
          </SelectedObjectiveTag>
        ))}
      </>
    );
  };

  const renderFormUI = () => (
    <StyledForm
      id="compliance-purpose-form"
      form={form}
      layout="vertical"
      requiredMark={false}
    >
      {/* Specific Fraud Risk Factors */}
      <Form.Item
        name="specificFraudRiskFactors"
        rules={[
          { required: true, message: 'Please enter specific fraud risk factors' },
          { whitespace: true, message: 'Specific fraud risk factors cannot be empty' },
        ]}
      >
        <div>
          <FormLabel>Specific Fraud Risk Factors</FormLabel>
          <StyledTextArea
            value={specificFraudRiskFactors}
            onChange={(e) => setSpecificFraudRiskFactors(e.target.value)}
            placeholder="Enter specific fraud risk factors"
            rows={5}
            spellCheck={true}
          />
        </div>
      </Form.Item>

      {/* Objectives */}
      <Form.Item name="objectives">
        <div>
          <FormLabel>Objectives</FormLabel>
          {isLoadingObjectives ? (
            <LoadingWrapper>
              <div className="spinner" />
              <div className="text">Loading objectives...</div>
            </LoadingWrapper>
          ) : (
            <>
              <TagContainer onClick={() => enterObjectiveSearchMode()}>
                {renderObjectiveTags()}
                <ArrowButton
                  onClick={(e) => {
                    e.stopPropagation();
                    enterObjectiveSearchMode();
                  }}
                >
                  <RightOutlined />
                </ArrowButton>
              </TagContainer>
              <InfoText>
                Select objectives from any cycle and process
              </InfoText>
            </>
          )}
        </div>
      </Form.Item>
    </StyledForm>
  );

  return (
    <RightContentDrawer
      open={open}
      title={isSearchMode ? 'Select Objectives' : drawerTitle}
      width={572}
      onClose={handleDrawerClose}
      footer={
        isSearchMode ? (
          <ButtonGroup>
            <Button type="default" onClick={exitObjectiveSearchMode}>
              Go back
            </Button>
            <Button type="primary" onClick={applyObjectiveSelection}>
              Done
            </Button>
          </ButtonGroup>
        ) : (
          <ButtonGroup>
            <Button
              type="default"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleFormSubmit}
              loading={loading}
              disabled={!isFormValid || !isAdmin}
            >
              {loading ? 'Saving...' : mode === 'add' ? 'Add Factor' : 'Save Changes'}
            </Button>
          </ButtonGroup>
        )
      }
    >
      {isSearchMode ? renderObjectiveSearchUI() : renderFormUI()}
    </RightContentDrawer>
  );
};

export default CompliancePurposeItemDrawer;
